import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  CheckCircle, Circle, Loader2, Plus, XCircle, ChevronDown, ChevronUp,
  Home, FileText, Scale, Search, Award, ArrowRight, Clock, Briefcase,
  Upload, Paperclip, Download, File, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import LegalProgressBar from './LegalProgressBar';
import AISuccessPanel from './AISuccessPanel';
import { useCurrencySettings } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const milestoneIcons = {
  enquiry: <Search className="h-4 w-4" />,
  viewing_booked: <Clock className="h-4 w-4" />,
  viewing_completed: <Home className="h-4 w-4" />,
  offer_made: <FileText className="h-4 w-4" />,
  offer_accepted: <CheckCircle className="h-4 w-4" />,
  solicitor_engaged: <Scale className="h-4 w-4" />,
  survey_ordered: <Search className="h-4 w-4" />,
  survey_completed: <FileText className="h-4 w-4" />,
  mortgage_approved: <Briefcase className="h-4 w-4" />,
  contracts_exchanged: <Scale className="h-4 w-4" />,
  completion: <Award className="h-4 w-4" />,
  // Tenant-specific
  application_submitted: <FileText className="h-4 w-4" />,
  references_passed: <ShieldCheck className="h-4 w-4" />,
  agreement_signed: <Scale className="h-4 w-4" />,
  deposit_protected: <ShieldCheck className="h-4 w-4" />,
  keys_collected: <Award className="h-4 w-4" />,
};

const MILESTONES = [
  { key: "enquiry", label: "Enquiry Made", order: 1 },
  { key: "viewing_booked", label: "Viewing Booked", order: 2 },
  { key: "viewing_completed", label: "Viewing Completed", order: 3 },
  { key: "offer_made", label: "Offer Made", order: 4 },
  { key: "offer_accepted", label: "Offer Accepted", order: 5 },
  { key: "solicitor_engaged", label: "Solicitor Engaged", order: 6 },
  { key: "survey_ordered", label: "Survey Ordered", order: 7 },
  { key: "survey_completed", label: "Survey Completed", order: 8 },
  { key: "mortgage_approved", label: "Mortgage Approved", order: 9 },
  { key: "contracts_exchanged", label: "Contracts Exchanged", order: 10 },
  { key: "completion", label: "Completion", order: 11 },
];

const TENANT_MILESTONES = [
  { key: "enquiry", label: "Enquiry Made", order: 1 },
  { key: "viewing_booked", label: "Viewing Booked", order: 2 },
  { key: "viewing_completed", label: "Viewing Completed", order: 3 },
  { key: "application_submitted", label: "Application Submitted", order: 4 },
  { key: "references_passed", label: "References Passed", order: 5 },
  { key: "agreement_signed", label: "Tenancy Agreement Signed", order: 6 },
  { key: "deposit_protected", label: "Deposit Protected", order: 7 },
  { key: "keys_collected", label: "Keys Collected", order: 8 },
];

const DealCard = ({ deal, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { formatPrice: fmtCurrency } = useCurrencySettings();

  const isRental = deal.listing_type === 'rent';
  const activeMilestones = isRental ? TENANT_MILESTONES : MILESTONES;
  const formattedPrice = deal.property_price ? fmtCurrency(deal.property_price, deal.property_currency?.toUpperCase()) : '';

  const completedCount = activeMilestones.filter(m => deal.milestones?.[m.key]?.completed).length;
  const progress = Math.round((completedCount / activeMilestones.length) * 100);

  const toggleMilestone = async (key, currentState) => {
    setUpdating(key);
    try {
      
      await axios.put(`${API_URL}/api/deals/${deal.id}/milestone`, {
        milestone_key: key,
        completed: !currentState,
      });
      toast.success(`Milestone ${!currentState ? 'completed' : 'unchecked'}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  const cancelDeal = async () => {
    if (!window.confirm('Are you sure you want to cancel this deal?')) return;
    try {
      await axios.put(`${API_URL}/api/deals/${deal.id}/cancel`, {});
      toast.success('Deal cancelled');
      onRefresh();
    } catch (err) {
      toast.error('Failed to cancel deal');
    }
  };

  const deleteDeal = async () => {
    if (!window.confirm('Delete this deal permanently? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/deals/${deal.id}`);
      toast.success('Deal deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete deal');
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('milestone', deal.current_milestone || 'general');
    try {
      await axios.post(`${API_URL}/api/deals/${deal.id}/documents`, form);
      toast.success(`"${file.name}" uploaded to deal`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className={`luxury-card overflow-hidden ${deal.status === 'cancelled' ? 'opacity-60' : ''}`} data-testid={`deal-card-${deal.id}`}>
      {/* Header */}
      <div className="p-5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] truncate">{deal.property_title}</h4>
              <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold ${
                deal.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                deal.status === 'cancelled' ? 'bg-red-50 text-red-700 border border-red-200' :
                'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>{deal.status}</span>
              {deal.verified_offer && (
                <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1" data-testid="verified-offer-badge">
                  <ShieldCheck className="h-3 w-3" />Verified
                </span>
              )}
              {isRental && (
                <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase font-bold bg-violet-50 text-violet-700 border border-violet-200" data-testid="rental-deal-badge">
                  Rental
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {deal.property_city}, {deal.property_country} &bull; {formattedPrice}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-semibold text-[#E07A3A]">{progress}%</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{completedCount}/{activeMilestones.length}</p>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 mt-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${deal.status === 'completed' ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Expanded milestones */}
      {expanded && deal.status !== 'cancelled' && (
        <div className="border-t border-slate-100 p-5">
          <div className="space-y-0" data-testid="milestones-timeline">
            {activeMilestones.map((m, i) => {
              const data = deal.milestones?.[m.key] || {};
              const isCompleted = data.completed;
              const isUpdating = updating === m.key;
              return (
                <div key={m.key} className="flex items-start gap-3 relative">
                  {/* Timeline line */}
                  {i < activeMilestones.length - 1 && (
                    <div className={`absolute left-[11px] top-6 w-0.5 h-full ${isCompleted ? 'bg-[#E07A3A]' : 'bg-slate-200'}`} />
                  )}
                  {/* Circle / Check */}
                  <button
                    onClick={() => toggleMilestone(m.key, isCompleted)}
                    disabled={isUpdating || deal.status === 'completed'}
                    className="flex-shrink-0 mt-0.5 z-10"
                    data-testid={`milestone-toggle-${m.key}`}
                  >
                    {isUpdating ? (
                      <Loader2 className="h-6 w-6 text-[#E07A3A] animate-spin" />
                    ) : isCompleted ? (
                      <CheckCircle className="h-6 w-6 text-[#E07A3A]" />
                    ) : (
                      <Circle className="h-6 w-6 text-slate-300 hover:text-[#E07A3A] transition-colors" />
                    )}
                  </button>
                  {/* Content */}
                  <div className="flex-1 pb-5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isCompleted ? 'text-[#1A1A1A]' : 'text-slate-400'}`}>
                        {m.label}
                      </span>
                      <span className="text-slate-300">{milestoneIcons[m.key]}</span>
                    </div>
                    {data.completed_at && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(data.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {deal.status === 'active' && (
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
              <input type="file" ref={fileRef} onChange={handleDocUpload} accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" className="hidden" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-xs text-[#E07A3A] hover:text-[#C4652E] font-semibold"
                data-testid="deal-upload-doc-btn"
              >
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
              <button onClick={cancelDeal} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid="cancel-deal-btn">
                <XCircle className="h-3 w-3" /> Cancel Deal
              </button>
            </div>
          )}

          {/* Documents */}
          {deal.documents && deal.documents.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="deal-documents">
              <p className="text-[10px] tracking-widest text-slate-400 uppercase mb-2 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Documents ({deal.documents.length})
              </p>
              <div className="space-y-1.5">
                {deal.documents.map((doc, i) => (
                  <a
                    key={`${doc.url}-${i}`}
                    href={doc.url.startsWith('/') ? `${API_URL}${doc.url}` : doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-slate-100 transition-colors group"
                    data-testid={`deal-doc-${i}`}
                  >
                    <File className="h-4 w-4 text-[#E07A3A] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#1A1A1A] font-medium truncate">{doc.filename}</p>
                      <p className="text-[10px] text-slate-400">{doc.uploaded_by_name} &bull; {doc.milestone} &bull; {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</p>
                    </div>
                    <Download className="h-3.5 w-3.5 text-slate-300 group-hover:text-[#E07A3A]" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Legal Progress Bar */}
          <LegalProgressBar dealId={deal.id} />

          {/* Deal Actions */}
          {deal.status === 'active' && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3" data-testid="deal-actions">
              <button onClick={cancelDeal} className="text-xs text-amber-600 hover:text-amber-700 font-semibold tracking-wider uppercase" data-testid="cancel-deal-btn">
                Cancel Deal
              </button>
              <span className="text-slate-200">|</span>
              <button onClick={deleteDeal} className="text-xs text-red-500 hover:text-red-600 font-semibold tracking-wider uppercase" data-testid="delete-deal-btn">
                Delete Permanently
              </button>
            </div>
          )}
          {(deal.status === 'cancelled' || deal.status === 'completed') && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="deal-actions">
              <button onClick={deleteDeal} className="text-xs text-red-500 hover:text-red-600 font-semibold tracking-wider uppercase" data-testid="delete-deal-btn">
                Delete Deal
              </button>
            </div>
          )}

          {/* AI Customer Success Panel */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <AISuccessPanel
              dealId={deal.id}
              listingType={deal.listing_type || 'sale'}
              country={deal.property_country || 'UK'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DealTracker = ({ userRole = 'buyer' }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchDeals = useCallback(async () => {
    try {
      
      const res = await axios.get(`${API_URL}/api/deals`);
      setDeals(res.data || []);
    } catch {
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const canCreateDeals = userRole === 'buyer' || userRole === 'tenant';

  const fetchProperties = useCallback(async () => {
    if (!canCreateDeals) { setProperties([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/properties`);
      const all = res.data || [];
      // Filter: tenants see only rent, buyers see only sale
      if (userRole === 'tenant') {
        setProperties(all.filter(p => p.listing_type === 'rent'));
      } else {
        setProperties(all.filter(p => p.listing_type === 'sale'));
      }
    } catch { setProperties([]); }
  }, [userRole, canCreateDeals]);

  useEffect(() => {
    fetchDeals();
    fetchProperties();
  }, [fetchDeals, fetchProperties]);

  const createDeal = async () => {
    if (!selectedProperty) {
      toast.error('Select a property');
      return;
    }
    setCreating(true);
    try {
      
      await axios.post(`${API_URL}/api/deals`, {
        property_id: selectedProperty,
      });
      toast.success('Deal created');
      setShowCreate(false);
      setSelectedProperty('');
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deal');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  const activeDeals = deals.filter(d => d.status === 'active');
  const completedDeals = deals.filter(d => d.status === 'completed');
  const cancelledDeals = deals.filter(d => d.status === 'cancelled');

  return (
    <div className="space-y-6" data-testid="deal-tracker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Deal Tracker</h2>
          <p className="text-slate-500 text-sm mt-1">
            {userRole === 'seller' || userRole === 'agent'
              ? 'Manage deals initiated by buyers and tenants on your properties'
              : userRole === 'tenant'
              ? 'Track your rental applications from enquiry to keys collected'
              : 'Track your property purchases from enquiry to completion'}
          </p>
        </div>
        {canCreateDeals && (
          <Button
            onClick={() => setShowCreate(!showCreate)}
            className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-sm tracking-wider"
            data-testid="start-deal-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Start Deal
          </Button>
        )}
      </div>

      {/* Create Deal */}
      {showCreate && canCreateDeals && (
        <div className="luxury-card p-6 space-y-4" data-testid="create-deal-form">
          <h3 className="font-semibold text-[#1A1A1A] text-sm tracking-wider uppercase">
            {userRole === 'tenant' ? 'Start a Rental Application' : 'Start a Property Purchase'}
          </h3>
          <p className="text-xs text-slate-400">
            {userRole === 'tenant'
              ? 'Select a rental property to begin your tenancy journey (Enquiry → References → Agreement → Keys)'
              : 'Select a property for sale to begin your purchase journey (Enquiry → Offer → Exchange → Completion)'}
          </p>
          {properties.length === 0 ? (
            <div className="p-4 bg-slate-50 border border-slate-200 text-center">
              <p className="text-sm text-slate-500">{userRole === 'tenant' ? 'No rental properties available right now' : 'No sale properties available right now'}</p>
            </div>
          ) : (
            <>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full h-12 bg-[#F8FAFC] border border-slate-200 px-4 text-[#1A1A1A] focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
                data-testid="deal-property-select"
              >
                <option value="">Select a property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.title} — {p.city} ({p.listing_type === 'rent' ? 'Rental' : 'Sale'})</option>
                ))}
              </select>
              <div className="flex gap-3">
                <Button onClick={createDeal} disabled={creating || !selectedProperty}
                  className="bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="confirm-deal-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                  {userRole === 'tenant' ? 'Start Rental Application' : 'Start Purchase Deal'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Active Deals */}
      {activeDeals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs text-slate-500 tracking-wider uppercase font-bold flex items-center gap-2">
            <ArrowRight className="h-3 w-3 text-[#E07A3A]" /> Active Deals ({activeDeals.length})
          </h3>
          {activeDeals.map(deal => <DealCard key={deal.id} deal={deal} onRefresh={fetchDeals} />)}
        </div>
      )}

      {/* Completed Deals */}
      {completedDeals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs text-slate-500 tracking-wider uppercase font-bold flex items-center gap-2">
            <CheckCircle className="h-3 w-3 text-emerald-500" /> Completed ({completedDeals.length})
          </h3>
          {completedDeals.map(deal => <DealCard key={deal.id} deal={deal} onRefresh={fetchDeals} />)}
        </div>
      )}

      {/* Cancelled */}
      {cancelledDeals.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs text-slate-500 tracking-wider uppercase font-bold flex items-center gap-2">
            <XCircle className="h-3 w-3 text-red-400" /> Cancelled ({cancelledDeals.length})
          </h3>
          {cancelledDeals.map(deal => <DealCard key={deal.id} deal={deal} onRefresh={fetchDeals} />)}
        </div>
      )}

      {/* Empty state */}
      {deals.length === 0 && (
        <div className="luxury-card p-12 text-center">
          <Award className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">
            {userRole === 'seller' || userRole === 'agent' ? 'No deals on your properties yet' : 'No deals yet'}
          </p>
          <p className="text-slate-400 text-sm mt-1">
            {userRole === 'seller' || userRole === 'agent'
              ? 'Deals appear here when buyers or tenants start a transaction on your listings'
              : userRole === 'tenant'
              ? 'Browse rental properties and click "Start Deal" to begin your tenancy application'
              : 'Browse properties for sale and click "Start Deal" to begin your purchase journey'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DealTracker;
